import { useTranslation } from "react-i18next";
import type { BudgetReportResponse, BudgetReportUnitResponse } from "../../api/organisationReporting";
import { formatRoundedMoney } from "../consumption/formatters";
import { HardStopIcon } from "../budgets/BudgetCellContent";
import { OrganisationSection } from "./OrganisationSection";
import { budgetStanding, budgetUnitName, sectionIds } from "./organisationInsights";
import styles from "./OrganisationBudgets.module.css";

/**
 * The Budgets section of the Organization page (GH-135, R-29), straight under the hero: the one finding a budget holder wants first
 * (how many units are over, and which is furthest over, or which is closest to its budget), then every unit with a budget for the
 * month, over-budget units first, with its budget (set on it or implied by the units beneath; the page does not say which), its spend, its Capex spend, the difference and its hard
 * stop. The spend of people in no group is one line beneath, since it is in no unit's figures (R-21). The report's order is kept
 * within each group; over-budget rows are moved up.
 *
 * @param props.report - The month's budget report.
 */
export function OrganisationBudgets({ report }: { report: BudgetReportResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const headingId = "organisation-budgets-heading";
  // Over-budget first. The sort is stable, so the report's own order (largest overspend first) holds within each group.
  const rows = report.units
    .filter((unit) => unit.budgetUsd !== null)
    .sort((left, right) => Number(right.overBudget) - Number(left.overBudget));
  const money = (amount: number): string => formatRoundedMoney(amount, report.currency);
  // Many groups share a name ("General"), so a group is named with the unit it sits under.
  const namesById = new Map(report.units.map((unit) => [unit.id.toLowerCase(), unit.name]));
  const parentName = (unit: BudgetReportUnitResponse): string | null => unit.kind === "Group" && unit.parentId ? namesById.get(unit.parentId.toLowerCase()) ?? null : null;
  const standing = budgetStanding(report);
  let headline: string | null = null;
  if (standing?.furthestOver) {
    headline = t("budgets.organisation.headline.over", {
      count: standing.overCount,
      budgeted: standing.budgeted,
      name: budgetUnitName(report, standing.furthestOver.unit),
      amount: money(standing.furthestOver.overBy),
      budget: money(standing.furthestOver.unit.budgetUsd ?? 0),
    });
  } else if (standing?.mostUsed) {
    headline = t("budgets.organisation.headline.within", {
      count: standing.budgeted,
      name: budgetUnitName(report, standing.mostUsed.unit),
      percent: Math.round(standing.mostUsed.usedPercent),
    });
  }

  return (
    <div className={styles.wide}>
      <OrganisationSection
        icon="budget"
        headingId={headingId}
        sectionId={sectionIds.budget}
        title={t("budgets.organisation.title")}
        subtitle={t("budgets.organisation.subtitle")}
      >
        {headline && <p className={styles.headline}>{headline}</p>}
        <p className={styles.attribution}>{t("budgets.organisation.attribution")}</p>
        <p className={styles.attribution}>{report.capexCountsAgainstBudget ? t("budgets.organisation.capexCounts") : t("budgets.organisation.capexExcluded")}</p>
        {rows.length === 0 ? (
          <p className={styles.note}>{t("budgets.organisation.empty")}</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-labelledby={headingId}>
              <thead>
                <tr>
                  <th scope="col">{t("budgets.organisation.columns.unit")}</th>
                  <th scope="col">{t("budgets.organisation.columns.budget")}</th>
                  <th scope="col">{t("budgets.organisation.columns.spend")}</th>
                  <th scope="col">{t("budgets.organisation.columns.capexSpend")}</th>
                  <th scope="col">{t("budgets.organisation.columns.difference")}</th>
                  <th scope="col">{t("budgets.organisation.columns.hardStop")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((unit) => (
                  <BudgetRow key={`${unit.kind}:${unit.id}`} unit={unit} parentName={parentName(unit)} money={money} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={styles.notInAGroup}>
          <span className={styles.notInAGroupLabel}>{t("budgets.notInAGroup")}</span>{" "}
          <strong className={styles.notInAGroupAmount}>{money(report.notInAGroupSpendUsd)}</strong>
          <span className={styles.note}>{t("budgets.organisation.notInAGroupNote")}</span>
        </p>
      </OrganisationSection>
    </div>
  );
}

/** One unit's row: its name (a group's with the unit above it) and kind, its budget, its spend, the difference against the budget, and its hard stop. */
function BudgetRow({ unit, parentName, money }: { unit: BudgetReportUnitResponse; parentName: string | null; money: (amount: number) => string }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <tr className={unit.overBudget ? styles.overRow : undefined}>
      <th scope="row">
        {parentName && <span className={styles.parentName}>{parentName} / </span>}
        <span className={styles.unitName}>{unit.name}</span>{" "}
        <span className={styles.kind}>{t(`budgets.kind.${unit.kind}`)}</span>
      </th>
      <td className={styles.number}>
        {unit.budgetUsd === null ? null : money(unit.budgetUsd)}
      </td>
      <td className={styles.number}>{money(unit.spendUsd)}</td>
      <td className={styles.number}>{unit.capexSpendUsd > 0 ? money(unit.capexSpendUsd) : null}</td>
      <td className={styles.number}>
        {unit.differenceUsd !== null && (
          <span className={unit.overBudget ? styles.overAmount : undefined}>
            {unit.differenceUsd < 0 ? t("budgets.organisation.overBy", { amount: money(-unit.differenceUsd) }) : t("budgets.organisation.underBy", { amount: money(unit.differenceUsd) })}
          </span>
        )}
      </td>
      <td>
        {unit.effectiveHardStop
          ? <span className={styles.hardStop}><span aria-hidden="true"><HardStopIcon /></span> {t("budgets.hardStop.label")}</span>
          : <span className="visually-hidden">{t("budgets.hardStop.off")}</span>}
      </td>
    </tr>
  );
}
